// Website status enum
export enum WebsiteStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  MAINTENANCE = 'maintenance'
}


// Website type enum
export enum WebsiteType {
  DTC = 'DTC',
  B2B = 'B2B',
  MARKETPLACE = 'Marketplace',
  UNKNOWN = 'Unknown'
}

// Status type enum for timeline
export enum StatusType {
  COMPLETED = 'completed',
  PENDING = 'pending',
  ERROR = 'error'
}

export enum PrimitiveWorkflowStatus {
  FAILED = 'FAILED',
  SUCCEEDED = 'SUCCEEDED',
  RUNNING = 'RUNNING'
}

// Query keys for React Query
export const QUERY_KEYS = {
  WEBSITE_SEARCH: 'websiteSearch',
  WEBSITE_METADATA: 'websiteMetadata',
  TIMELINE_DATA: 'timelineData',
  POINT_IN_TIME_METRICS: 'pointInTimeMetrics',
  WEBSITE_SUMMARY: 'websiteSummary'
} as const;

// Cache times for React Query (in milliseconds)
export const CACHE_TIMES = {
  STALE_TIME: 5 * 60 * 1000, // 5 minutes
  CACHE_TIME: 10 * 60 * 1000, // 10 minutes
} as const;

// UI Constants
export const UI_CONSTANTS = {
  DROPDOWN_MAX_HEIGHT: 300,
  SEARCH_DEBOUNCE_MS: 300,
  DEFAULT_SEARCH_RESULTS: 5,
  CHIP_HEIGHT: 20,
  ICON_SIZE: {
    SMALL: 16,
    MEDIUM: 20,
    LARGE: 24
  },
  SELECTOR: {
    MIN_WIDTH: 500,
    TOKEN_LIMIT: 4
  }
} as const;

// Selector constants
export const SELECTOR_CONSTANTS = {
  PROPERTY_KEYS: {
    DOMAIN: 'eventAttributes.domain',
    WEBSITE_ID: 'eventAttributes.websiteId'
  },
  OPERATORS: {
    EQUALS: '='
  },
  FILTER_OPERATION: 'and',
  DEFAULT_SEARCH_PROPERTY: 'eventAttributes.domain'
} as const;

// i18n strings for PropertyFilter
export const PROPERTY_FILTER_I18N = {
  filteringAriaLabel: "Type in to search websites by either name or id",
  filteringPlaceholder: "Type in to search websites by either name or id",
  cancelActionText: "Cancel",
  applyActionText: "Apply",
  allPropertiesLabel: "All",
  clearFiltersText: "Clear Filter",
  operatorsText: "Operators",
  operationAndText: "AND",
  operationOrText: "OR",
  operatorLessText: "Less than",
  operatorLessOrEqualText: "Less than or equal to",
  operatorGreaterText: "Greater than",
  operatorGreaterOrEqualText: "Greater than or equal to",
  operatorContainsText: "Regex match",
  operatorEqualsText: "Equal to",
  operatorDoesNotEqualText: "Not equal to",
  tokenLimitShowMore: "Show More",
  tokenLimitShowFewer: "Show fewer",
  propertyText: "Property filters",
  groupPropertiesText: "Property filters"
} as const;

// Filtering properties configuration
export const FILTERING_PROPERTIES = [
  {
    key: SELECTOR_CONSTANTS.PROPERTY_KEYS.DOMAIN,
    propertyLabel: 'Website Name',
    operators: [SELECTOR_CONSTANTS.OPERATORS.EQUALS],
    groupValuesLabel: 'Search Property'
  },
  {
    key: SELECTOR_CONSTANTS.PROPERTY_KEYS.WEBSITE_ID,
    propertyLabel: 'Website Id',
    operators: [SELECTOR_CONSTANTS.OPERATORS.EQUALS],
    groupValuesLabel: 'Search Property'
  }
] as const;

// Tooltip configuration
export const SELECTOR_TOOLTIP = {
  title: "Website Dashboard Search",
  description: "Search websites by name or ID from Program Source repository.",
  placement: "top" as const
} as const;

// URL parameter names
export const URL_PARAMS = {
  WEBSITE: 'website',
  WEBSITE_ID: 'websiteId',
  SEARCH_PROP: 'searchProp'
} as const;

// Status colors mapping
export const STATUS_COLORS = {
  [WebsiteStatus.ACTIVE]: {
    backgroundColor: '#c8e6c9',
    color: '#2e7d32'
  },
  [WebsiteStatus.MAINTENANCE]: {
    backgroundColor: '#fff3cd',
    color: '#856404'
  },
  [WebsiteStatus.INACTIVE]: {
    backgroundColor: '#e0e0e0',
    color: '#616161'
  }
} as const;

export enum PRIMITIVE_TRIGGER_TYPES {
  SYSTEM = 'SYSTEM',
  MANUAL = 'MANUAL'
}


// Timeline status colors
export const TIMELINE_STATUS_COLORS = {
  [StatusType.COMPLETED]: '#4caf50',
  [StatusType.PENDING]: '#ff9800',
  [StatusType.ERROR]: '#f44336'
} as const;

// Metadata field keys enum
export enum MetadataFieldKey {
  WEBSITE_ID = 'WEBSITE_ID',
  WEBSITE_TYPE = 'WEBSITE_TYPE',
  PROGRAM = 'PROGRAM',
  SUB_PROGRAM = 'SUB_PROGRAM',
  SELECTION_SIZE = 'SELECTION_SIZE',
  PLATFORM = 'PLATFORM',
  OPERATING_COUNTRIES = 'OPERATING_COUNTRIES',
  COUNTRY_OF_ORIGIN = 'COUNTRY_OF_ORIGIN'
}

// Metadata field labels
export const METADATA_FIELDS = {
  [MetadataFieldKey.WEBSITE_ID]: 'Website Id',
  [MetadataFieldKey.WEBSITE_TYPE]: 'Website Type',
  [MetadataFieldKey.PROGRAM]: 'Program',
  [MetadataFieldKey.SUB_PROGRAM]: 'SubProgram',
  [MetadataFieldKey.SELECTION_SIZE]: 'Est. Selection Size',
  [MetadataFieldKey.PLATFORM]: 'Platform',
  [MetadataFieldKey.OPERATING_COUNTRIES]: 'Operating Countries',
  [MetadataFieldKey.COUNTRY_OF_ORIGIN]: 'Country of Origin'
} as const;

// Metadata field colors
export const METADATA_FIELD_COLORS = {
  [MetadataFieldKey.WEBSITE_ID]: '#9C27B0',
  [MetadataFieldKey.WEBSITE_TYPE]: '#2196F3',
  [MetadataFieldKey.PROGRAM]: '#4CAF50',
  [MetadataFieldKey.SUB_PROGRAM]: '#FF9800',
  [MetadataFieldKey.SELECTION_SIZE]: '#4CAF50',
  [MetadataFieldKey.PLATFORM]: '#4CAF50',
  [MetadataFieldKey.OPERATING_COUNTRIES]: '#00BCD4',
  [MetadataFieldKey.COUNTRY_OF_ORIGIN]: '#E91E63'
} as const;

// Metadata field icon names (to be used with Material-UI icons)
export const METADATA_FIELD_ICONS = {
  [MetadataFieldKey.WEBSITE_ID]: 'FingerprintOutlined',
  [MetadataFieldKey.WEBSITE_TYPE]: 'LanguageOutlined',
  [MetadataFieldKey.PROGRAM]: 'LayersOutlined',
  [MetadataFieldKey.SUB_PROGRAM]: 'LayersOutlined',
  [MetadataFieldKey.SELECTION_SIZE]: 'PhotoSizeSelectActualOutlined',
  [MetadataFieldKey.PLATFORM]: 'ShoppingCartOutlined',
  [MetadataFieldKey.OPERATING_COUNTRIES]: 'PublicOutlined',
  [MetadataFieldKey.COUNTRY_OF_ORIGIN]: 'FlagOutlined'
} as const;

// Default values
export const DEFAULT_VALUES = {
  [MetadataFieldKey.WEBSITE_ID]: '0000000',
  [MetadataFieldKey.WEBSITE_TYPE]: WebsiteType.UNKNOWN,
  [MetadataFieldKey.PROGRAM]: 'Not Available',
  [MetadataFieldKey.SUB_PROGRAM]: 'N/A',
  [MetadataFieldKey.SELECTION_SIZE]: '2',
  [MetadataFieldKey.PLATFORM]: 'Unknown',
  [MetadataFieldKey.OPERATING_COUNTRIES]: 'Not Available',
  [MetadataFieldKey.COUNTRY_OF_ORIGIN]: 'Not Available'
} as const;

export const METADATA_FIELD_TOOLTIP_DESCRIPTIONS = {
  [MetadataFieldKey.WEBSITE_ID]: 'Sourced from EWC',
  [MetadataFieldKey.WEBSITE_TYPE]: 'Sourced from EWC',
  [MetadataFieldKey.PROGRAM]: 'Sourced from Program Source repository',
  [MetadataFieldKey.SUB_PROGRAM]: 'Sourced from Program Source repository',
  [MetadataFieldKey.SELECTION_SIZE]: 'Sourced from EWC',
  [MetadataFieldKey.PLATFORM]: 'Sourced from EWC',
  [MetadataFieldKey.OPERATING_COUNTRIES]: 'Sourced from EWC',
  [MetadataFieldKey.COUNTRY_OF_ORIGIN]: 'Sourced from EWC'
} as const;
